import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Subscription } from 'rxjs';
import { AppStateService } from '../../services/app-state.service';
import { CmsApiResponse, CmsPageService } from '../../services/cms-page.service';
import { environment } from '../../../environments/environment';
import { ContactCmsContent } from '../../models/types';
import {
  DEFAULT_CONTACT_CMS,
  parseContactCmsContent,
} from '../../config/contact-cms.defaults';

@Component({
  selector: 'app-contact-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './contact-page.component.html',
})
export class ContactPageComponent implements OnInit, OnDestroy {
  state = inject(AppStateService);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private cmsPageService = inject(CmsPageService);
  private routeSub?: Subscription;

  loading = signal(true);
  cms = signal<ContactCmsContent>(DEFAULT_CONTACT_CMS);

  email = '';
  subject = '';
  message = '';
  attachment: File | null = null;
  attachmentName = '';
  humanVerified = false;
  isSubmitting = signal(false);

  ngOnInit() {
    this.routeSub = this.route.data.subscribe((data) => {
      this.applyCmsResponse(data['cmsResponse'] as CmsApiResponse | null);
    });
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
  }

  private applyCmsResponse(res: CmsApiResponse | null) {
    this.loading.set(true);

    const content =
      res?.status && res.data?.content
        ? parseContactCmsContent(res.data.content)
        : DEFAULT_CONTACT_CMS;

    this.cms.set(content);
    this.cmsPageService.applyPageSeo(content.seo);
    this.loading.set(false);
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.attachment = file;
    this.attachmentName = file?.name ?? '';
  }

  async submit() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.email)) {
      this.state.triggerNotification('Please enter a valid email address.', false);
      return;
    }
    if (!this.subject.trim()) {
      this.state.triggerNotification('Please enter a subject.', false);
      return;
    }
    if (!this.message.trim()) {
      this.state.triggerNotification('Please enter your message.', false);
      return;
    }
    if (!this.attachment) {
      this.state.triggerNotification('Please attach a file (JPG, PNG, GIF, PDF, DOC).', false);
      return;
    }
    if (!this.humanVerified) {
      this.state.triggerNotification('Please confirm you are not a robot.', false);
      return;
    }

    const formData = new FormData();
    formData.append('email', this.email.trim());
    formData.append('subject', this.subject.trim());
    formData.append('message', this.message.trim());
    formData.append('attachment', this.attachment);
    formData.append('recaptchaToken', 'client-verified');

    this.isSubmitting.set(true);
    try {
      const res: any = await firstValueFrom(
        this.http.post(`${environment.apiUrl}/user/contact-us/create`, formData)
      );
      if (res?.status) {
        this.state.triggerNotification(res.message || 'Message sent successfully!');
        this.resetForm();
      } else {
        this.state.triggerNotification(res?.message || 'Failed to send message.', false);
      }
    } catch (err: any) {
      const errors = err?.error?.errors;
      const msg = errors
        ? Object.values(errors).join(' ')
        : err?.error?.message || 'Failed to submit. Please try again.';
      this.state.triggerNotification(String(msg), false);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private resetForm() {
    this.email = '';
    this.subject = '';
    this.message = '';
    this.attachment = null;
    this.attachmentName = '';
    this.humanVerified = false;
  }
}
